import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Company } from 'src/app/models/customer.model';
import { BuyerService } from 'src/app/services/buyer.service';
import { CountriesService } from 'src/app/services/countries.service';
import { Buyer } from 'src/app/models/buyer.model';
import { FilterOptions } from 'src/app/models/filter.model';
import { PaginationData } from 'src/app/models/helpers.model';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  takeUntil,
  combineLatestWith,
} from 'rxjs';
import { Country } from 'src/app/models/country.model';
import { FilterOperator } from 'src/app/enums/filter-operator';
import { HeaderStateService } from 'src/app/services/header-state.service';
@Component({
  selector: 'app-customer-buyers',
  templateUrl: './customer-buyers.component.html',
  styleUrls: ['./customer-buyers.component.scss'],
})
export class CustomerBuyersComponent implements OnInit, OnDestroy {
  @Input() customer!: Company;
  @Input() isAdmin!: boolean;
  buyers!: Buyer[];
  tableColumns = [
    'company',
    'person',
    'phone',
    'destination country',
    'destination address',
    'total',
  ];
  tableFilterColumns = [
    'company',
    'person',
    'phone',
    'destination country',
    'destination address',
    'total',
  ];
  columnsStates = [true, true, true, true, true, true, true];
  columnsStatesBackup = [true, true, true, true, true, true, true];
  paginationData!: PaginationData;
  searchOptions: FilterOptions = {
    advancedSearch: {
      fields: [
        'name',
        'phone',
        'firstName',
        'lastNAme',
        'email',
        'address',
        'country.name',
      ],
      keyword: '',
    },
    keyword: '',
    advancedFilter: null,
    pageNumber: 1,
    pageSize: 15,
    orderBy: ['name'],
  };
  customerFilter!: {
    field: string;
    operator: string;
    value: string;
  };
  searchString = '';
  isNewBuyer = false;
  lastColumnIndex!: number | null;
  dataSource: any;
  selectedBuyer!: Buyer | null;
  // The grid opens sorted by its leading column (COMPANY), ascending. Clicking that
  // header from here cycles asc -> desc -> unsorted, as it always has.
  selectedSort = { field: 'name', mode: 'asc' };
  private destroy = new Subject<boolean>();
  private search = new Subject<string>();

  constructor(
    private buyerService: BuyerService,
    private countriesService: CountriesService,
    private headerState: HeaderStateService
  ) {}

  ngOnInit(): void {
    if (this.isAdmin) {
      this.customerFilter = {
        field: 'companyId',
        operator: FilterOperator.Equals,
        value: this.customer.id,
      };
      this.searchOptions.advancedFilter = {
        logic: 'and',
        filters: [this.customerFilter],
      };
    }
    this.getBuyers(this.searchOptions);
    this.search.pipe(debounceTime(2000), distinctUntilChanged()).subscribe({
      next: () => {
        this.searchOptions.advancedSearch.keyword =
          this.searchString.toLowerCase();
        this.searchOptions.pageNumber = 1;
        this.getBuyers(this.searchOptions);
      },
    });
  }

  getBuyers(searchOptions: FilterOptions) {
    this.buyerService
      .getBuyers(searchOptions)
      .pipe(
        combineLatestWith(this.countriesService.contriesList),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([considneesResp, countries]) => {
          if (countries) {
            this.buyers = considneesResp.data.map(
              (buyer: Buyer) => {
                buyer.countryId = countries.find(
                  (country: Country) => country.id === buyer.countryId
                );
                return buyer;
              }
            );
          }
          const { data, ...clone } = considneesResp;
          this.paginationData = clone;
          this.dataSource = new MatTableDataSource(this.buyers);
          this.updateHeaderBadge();
        },
      });
  }

  selectBuyer(data: Buyer) {
    this.selectedBuyer = { ...data };
  }

  searchBuyer() {
    this.search.next(this.searchString.toLowerCase());
  }

  sortBuyers(field: string) {
    if (field !== this.selectedSort.field) {
      this.selectedSort = { field, mode: 'asc' };
      this.searchOptions.orderBy = [`${this.selectedSort.field}`];
      this.getBuyers(this.searchOptions);
      return;
    }
    if (this.selectedSort.mode === 'asc') {
      this.selectedSort = { field, mode: 'desc' };
      this.searchOptions.orderBy = [
        `${this.selectedSort.field} ${this.selectedSort.mode}`,
      ];
      this.getBuyers(this.searchOptions);
      return;
    }
    if (this.selectedSort.mode === 'desc') {
      this.selectedSort = { field: '', mode: '' };
      this.searchOptions.orderBy = [];
      this.getBuyers(this.searchOptions);
      return;
    }
  }

  toggleBuyerModal() {
    this.isNewBuyer = !this.isNewBuyer;
  }

  getPageSettings(pageSettings: { perPage: number; currentPage: number }) {
    this.searchOptions.pageSize = pageSettings.perPage;
    this.searchOptions.pageNumber = pageSettings.currentPage;
    this.getBuyers(this.searchOptions);
  }

  selectColumns(index?: number) {
    this.tableColumns = [...this.tableFilterColumns];
    if (index !== undefined) {
      this.columnsStatesBackup[index] = !this.columnsStatesBackup[index];
    } else {
      this.columnsStatesBackup = [true, true, true, true, true, true, true];
      this.columnsStates = [true, true, true, true, true, true, true];
    }
    let hiddenColumns: string[] = [];

    this.columnsStatesBackup.forEach((s, i) => {
      if (!s) {
        hiddenColumns.push(this.tableColumns[i]);
      }
    });

    this.tableColumns = this.tableColumns.filter(
      (t) => !hiddenColumns.includes(t)
    );

    if (this.tableColumns.length === 1) {
      this.lastColumnIndex = this.tableFilterColumns.indexOf(
        this.tableColumns[0]
      );
    } else {
      this.lastColumnIndex = null;
    }
  }

  updateState(data: { isDelete: boolean; isUpdate: boolean }) {
    this.selectedBuyer = null;

    if (data.isUpdate) {
      this.getBuyers(this.searchOptions);
      this.isNewBuyer = false;
    }
  }

  checkDisplaySelection(): boolean {
    return this.columnsStates.every((cs) => cs === true);
  }

  // Only the standalone page owns the header; inside the customer detail tabs the
  // badge belongs to the customer breadcrumb, not to the buyer list.
  private updateHeaderBadge(): void {
    if (this.isAdmin || !this.paginationData) {
      return;
    }
    const count = this.paginationData.totalCount;
    this.headerState.setBadge(
      `${count.toLocaleString('en-US')} ${
        count === 1 ? 'buyer' : 'buyers'
      }`
    );
  }

  ngOnDestroy(): void {
    if (!this.isAdmin) {
      this.headerState.setBadge(null);
    }
    this.destroy.next(true);
    this.destroy.complete();
  }
}
